import { createContext, useState, useMemo, useContext, useCallback } from 'react';

const ShiftsContext = createContext();

const BASE = process.env.REACT_APP_SERVICE_ENDPOINT;

export function ShiftsProvider(props) {
    const tokenSessionObject = JSON.parse(sessionStorage.getItem('token'));
    const token = tokenSessionObject?.token;

    // ── Data ──
    const [lines, setLines] = useState([]);
    const [selectedLine, setSelectedLine] = useState(null);
    const [lineName, setLineName] = useState('');
    const [turns, setTurns] = useState([]);
    const [selectedTurn, setSelectedTurn] = useState(null);
    const [schedules, setSchedules] = useState([]);

    // ── Loading ──
    const [loadingTurns, setLoadingTurns] = useState(false);
    const [loadingSchedules, setLoadingSchedules] = useState(false);
    const [saving, setSaving] = useState(false);

    // ── Snackbar ──
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const showSnackbar = useCallback((message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    }, []);
    const closeSnackbar = useCallback(() => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    }, []);

    // ── Dialog states ──
    const [dialogCreateTurn, setDialogCreateTurn] = useState(false);
    const [dialogEditTurn, setDialogEditTurn] = useState(false);
    const [dialogDeleteTurn, setDialogDeleteTurn] = useState(false);
    const [dialogCreateSchedule, setDialogCreateSchedule] = useState(false);
    const [dialogEditSchedule, setDialogEditSchedule] = useState(false);
    const [dialogDeleteSchedule, setDialogDeleteSchedule] = useState(false);
    const [editingTurn, setEditingTurn] = useState(null);
    const [editingSchedule, setEditingSchedule] = useState(null);
    const [deletingSchedule, setDeletingSchedule] = useState(null);

    const buildHeaders = useCallback(
        () => ({
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
        }),
        [token]
    );

    const handleApiError = useCallback(
        async (res, fallback) => {
            try {
                const body = await res.json();
                showSnackbar(body?.message || fallback, 'error');
            } catch {
                showSnackbar(fallback, 'error');
            }
        },
        [showSnackbar]
    );

    // ── Lines ──
    const getLines = useCallback(async () => {
        try {
            const res = await fetch(`${BASE}/lineas`, {
                method: 'GET',
                headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
            });
            if (!res.ok) throw new Error('Error al cargar líneas');
            setLines(await res.json());
        } catch (err) {
            showSnackbar(err.message || 'Error al cargar líneas', 'error');
        }
    }, [token, showSnackbar]);

    // ── Turnos ──
    const getTurnos = useCallback(
        async (linea) => {
            setLoadingTurns(true);
            try {
                const res = await fetch(`${BASE}/turnos/GetTurnos`, {
                    method: 'POST',
                    headers: buildHeaders(),
                    body: JSON.stringify({ linea }),
                });
                if (!res.ok) { await handleApiError(res, 'Error al cargar turnos'); setTurns([]); return; }
                const data = await res.json();
                setTurns(Array.isArray(data) ? data : []);
            } catch {
                showSnackbar('Error al cargar turnos', 'error');
                setTurns([]);
            } finally {
                setLoadingTurns(false);
            }
        },
        [buildHeaders, handleApiError, showSnackbar]
    );

    const crearTurno = useCallback(
        async (linea, turno, horaInicial) => {
            setSaving(true);
            try {
                const res = await fetch(`${BASE}/turnos/CrearTurno`, {
                    method: 'POST',
                    headers: buildHeaders(),
                    body: JSON.stringify({ linea, turno, horaInicial }),
                });
                if (!res.ok) { await handleApiError(res, 'Error al crear turno'); return; }
                showSnackbar('Turno creado correctamente');
                setDialogCreateTurn(false);
                await getTurnos(linea);
            } catch {
                showSnackbar('Error al crear turno', 'error');
            } finally {
                setSaving(false);
            }
        },
        [buildHeaders, handleApiError, showSnackbar, getTurnos]
    );

    const updateTurno = useCallback(
        async (linea, turno, horaInicial) => {
            setSaving(true);
            try {
                const res = await fetch(`${BASE}/turnos/UpdateTurno`, {
                    method: 'PUT',
                    headers: buildHeaders(),
                    body: JSON.stringify({ linea, turno, horaInicial }),
                });
                if (!res.ok) { await handleApiError(res, 'Error al actualizar turno'); return; }
                showSnackbar('Turno actualizado correctamente');
                setDialogEditTurn(false);
                setEditingTurn(null);
                await getTurnos(linea);
            } catch {
                showSnackbar('Error al actualizar turno', 'error');
            } finally {
                setSaving(false);
            }
        },
        [buildHeaders, handleApiError, showSnackbar, getTurnos]
    );

    const borrarTurno = useCallback(
        async (linea, turno) => {
            setSaving(true);
            try {
                const res = await fetch(`${BASE}/turnos/BorrarTurno`, {
                    method: 'DELETE',
                    headers: buildHeaders(),
                    body: JSON.stringify({ linea, turno }),
                });
                if (!res.ok) { await handleApiError(res, 'Error al eliminar turno'); return; }
                showSnackbar('Turno eliminado correctamente');
                setDialogDeleteTurn(false);
                setSelectedTurn(null);
                setSchedules([]);
                await getTurnos(linea);
            } catch {
                showSnackbar('Error al eliminar turno', 'error');
            } finally {
                setSaving(false);
            }
        },
        [buildHeaders, handleApiError, showSnackbar, getTurnos]
    );

    // ── Horarios ──
    const getHorarios = useCallback(
        async (linea, turno) => {
            setLoadingSchedules(true);
            try {
                const res = await fetch(`${BASE}/turnos/GetHorarios`, {
                    method: 'POST',
                    headers: buildHeaders(),
                    body: JSON.stringify({ linea, turno }),
                });
                if (!res.ok) { await handleApiError(res, 'Error al cargar horarios'); setSchedules([]); return; }
                const data = await res.json();
                setSchedules(Array.isArray(data) ? data : []);
            } catch {
                showSnackbar('Error al cargar horarios', 'error');
                setSchedules([]);
            } finally {
                setLoadingSchedules(false);
            }
        },
        [buildHeaders, handleApiError, showSnackbar]
    );

    const crearHorario = useCallback(
        async (linea, turno, horaIni, horaFin) => {
            setSaving(true);
            try {
                const res = await fetch(`${BASE}/turnos/CrearHorario`, {
                    method: 'POST',
                    headers: buildHeaders(),
                    body: JSON.stringify({ linea, turno, horaIni, horaFin }),
                });
                if (!res.ok) { await handleApiError(res, 'Error al crear horario'); return; }
                showSnackbar('Horario creado correctamente');
                setDialogCreateSchedule(false);
                await getHorarios(linea, turno);
            } catch {
                showSnackbar('Error al crear horario', 'error');
            } finally {
                setSaving(false);
            }
        },
        [buildHeaders, handleApiError, showSnackbar, getHorarios]
    );

    const updateHorario = useCallback(
        async (linea, turno, segmento, horaIni, horaFin) => {
            setSaving(true);
            try {
                const res = await fetch(`${BASE}/turnos/UpdateHorario`, {
                    method: 'PUT',
                    headers: buildHeaders(),
                    body: JSON.stringify({ linea, turno, segmento, horaIni, horaFin }),
                });
                if (!res.ok) { await handleApiError(res, 'Error al actualizar horario'); return; }
                showSnackbar('Horario actualizado correctamente');
                setDialogEditSchedule(false);
                setEditingSchedule(null);
                await getHorarios(linea, turno);
            } catch {
                showSnackbar('Error al actualizar horario', 'error');
            } finally {
                setSaving(false);
            }
        },
        [buildHeaders, handleApiError, showSnackbar, getHorarios]
    );

    const borrarHorario = useCallback(
        async (linea, turno, segmento) => {
            setSaving(true);
            try {
                const res = await fetch(`${BASE}/turnos/BorrarHorario`, {
                    method: 'DELETE',
                    headers: buildHeaders(),
                    body: JSON.stringify({ linea, turno, segmento }),
                });
                if (!res.ok) { await handleApiError(res, 'Error al eliminar horario'); return; }
                showSnackbar('Horario eliminado correctamente');
                setDialogDeleteSchedule(false);
                setDeletingSchedule(null);
                await getHorarios(linea, turno);
            } catch {
                showSnackbar('Error al eliminar horario', 'error');
            } finally {
                setSaving(false);
            }
        },
        [buildHeaders, handleApiError, showSnackbar, getHorarios]
    );

    const value = useMemo(
        () => ({
            lines,
            selectedLine, setSelectedLine,
            lineName, setLineName,
            turns,
            selectedTurn, setSelectedTurn,
            schedules, setSchedules,
            loadingTurns, loadingSchedules, saving,
            snackbar, showSnackbar, closeSnackbar,
            dialogCreateTurn, setDialogCreateTurn,
            dialogEditTurn, setDialogEditTurn,
            dialogDeleteTurn, setDialogDeleteTurn,
            dialogCreateSchedule, setDialogCreateSchedule,
            dialogEditSchedule, setDialogEditSchedule,
            dialogDeleteSchedule, setDialogDeleteSchedule,
            editingTurn, setEditingTurn,
            editingSchedule, setEditingSchedule,
            deletingSchedule, setDeletingSchedule,
            getLines, getTurnos, crearTurno, updateTurno, borrarTurno,
            getHorarios, crearHorario, updateHorario, borrarHorario,
        }),
        [
            lines, selectedLine, lineName, turns, selectedTurn, schedules,
            loadingTurns, loadingSchedules, saving,
            snackbar, showSnackbar, closeSnackbar,
            dialogCreateTurn, dialogEditTurn, dialogDeleteTurn,
            dialogCreateSchedule, dialogEditSchedule, dialogDeleteSchedule,
            editingTurn, editingSchedule, deletingSchedule,
            getLines, getTurnos, crearTurno, updateTurno, borrarTurno,
            getHorarios, crearHorario, updateHorario, borrarHorario,
        ]
    );

    return <ShiftsContext.Provider value={value} {...props} />;
}

export function useShifts() {
    const context = useContext(ShiftsContext);
    if (!context) throw new Error('useShifts debe estar dentro del proveedor ShiftsContext');
    return context;
}
